import type {
  EIP6963AnnounceProviderEvent,
  MetaMaskInpageProvider,
} from '@metamask/providers';

/**
 * Check if the current provider supports snaps by calling `wallet_getSnaps`.
 *
 * @param provider - The provider to use to check for snaps support. Defaults to
 * `window.ethereum`.
 * @returns True if the provider supports snaps, false otherwise.
 */
export async function hasSnapsSupport(
  provider: MetaMaskInpageProvider = window.ethereum,
) {
  try {
    await provider.request({
      method: 'wallet_getSnaps',
    });

    return true;
  } catch {
    return false;
  }
}

/**
 * Check if a provider is MetaMask Flask by inspecting its client version.
 *
 * @param provider - The provider to check.
 * @returns True if the provider reports itself as Flask, false otherwise.
 */
export async function isFlaskProvider(provider: MetaMaskInpageProvider) {
  try {
    const clientVersion = await provider.request({
      method: 'web3_clientVersion',
    });

    return typeof clientVersion === 'string' && clientVersion.includes('flask');
  } catch {
    return false;
  }
}

/**
 * Get MetaMask providers using EIP6963. This collects every provider
 * announcing as MetaMask within 500ms, listing Flask first so callers that
 * need it (locally-served snaps only install on Flask) find it even when
 * the regular MetaMask extension is also enabled and owns `window.ethereum`.
 *
 * @returns The discovered MetaMask providers, Flask first (possibly empty).
 */
export async function getMetaMaskEIP6963Providers() {
  return new Promise<MetaMaskInpageProvider[]>((resolve) => {
    const providers: MetaMaskInpageProvider[] = [];

    /**
     * Listener for the EIP6963 announceProvider event.
     *
     * Collects every provider reporting as MetaMask.
     *
     * @param event - The EIP6963 announceProvider event.
     * @param event.detail - The details of the EIP6963 announceProvider event.
     */
    function onAnnounceProvider({ detail }: EIP6963AnnounceProviderEvent) {
      const { info, provider } = detail;

      if (info.rdns.includes('io.metamask')) {
        if (info.rdns.includes('flask')) {
          providers.unshift(provider);
        } else {
          providers.push(provider);
        }
      }
    }

    setTimeout(() => {
      window.removeEventListener(
        'eip6963:announceProvider',
        onAnnounceProvider,
      );
      resolve(providers);
    }, 500);

    window.addEventListener('eip6963:announceProvider', onAnnounceProvider);

    window.dispatchEvent(new Event('eip6963:requestProvider'));
  });
}

/**
 * Get a provider that supports snaps. This checks every detected provider
 * (window.ethereum, multiplexed providers and EIP6963 announcements) and
 * returns a Flask provider when one is present — the regular MetaMask
 * extension also supports snaps but refuses locally-served ones, so with
 * both extensions enabled Flask must win. Falls back to the first provider
 * with snaps support.
 *
 * @returns The provider, or `null` if no provider supports snaps.
 */
export async function getSnapsProvider() {
  if (typeof window === 'undefined') {
    return null;
  }

  const candidates: MetaMaskInpageProvider[] = [];

  if (window.ethereum) {
    candidates.push(window.ethereum);
  }

  if (window.ethereum?.detected) {
    candidates.push(...window.ethereum.detected);
  }

  if (window.ethereum?.providers) {
    candidates.push(...window.ethereum.providers);
  }

  candidates.push(...(await getMetaMaskEIP6963Providers()));

  let fallback: MetaMaskInpageProvider | null = null;
  for (const provider of candidates) {
    if (await hasSnapsSupport(provider)) {
      if (await isFlaskProvider(provider)) {
        return provider;
      }
      fallback = fallback ?? provider;
    }
  }

  return fallback;
}
